import {
  AlertTriangle,
  Check,
  CheckCircle2,
  ChevronRight,
  Copy,
  Hourglass,
  Link2,
} from 'lucide-react';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import React from 'react';

import { getCurrentUserId } from 'common-src/features/auth';
import { getDashboardClients } from 'common-src/models/Client';
import { getTaskReasonById } from 'common-src/models/PatientTask';

import {
  getCareOrchestrationCapacityTasks,
  getCareOrchestrationCapacityTasksLoaded,
} from 'src/features/careOrchestration';
import useAlert from 'src/hooks/useAlert';
import useCustomSelector from 'src/hooks/useCustomSelector';

import { getStaffDisplayName, getStaffRole } from '../../balanceAssign';
import { PRIORITY_BADGE_COPY, TASK_DISPLAY_STATUS, TASK_PRIORITY, teamMeta } from '../../constants';
import {
  copyTextToClipboard,
  formatDue,
  getPatient,
  getTaskDeepLinkUrl,
  getTaskDisplayStatus,
  getTaskDisplayStatusLabel,
  getTaskDisplayTitle,
  isAssessmentTask,
  isAutomaticWorkflowTask,
  isLinkedTask,
  isMessageTask,
  isTaskBlockedStatus,
  isTaskCanceled,
  isTaskEscalatedStatus,
  isTaskUnableToComplete,
  isWorkflowBlockedTask,
} from '../../helpers';
import { detectNewBreach, projectClaim } from '../../hudSelectors';
import { Badge } from '../Badge';
import styles from './TaskRow.module.scss';

const TaskRow = ({
  task,
  compact,
  onSelect,
  selected,
  onAssignToMe,
  isAssigning,
  isLinkedChild,
  isSubtaskChild,
  isSubtask,
  parentTitle,
  linkedParentTitle,
  linkedChildCount,
  subtaskChildCount,
  delegatedSubtaskMeta,
  linkedExpanded,
  onToggleLinked,
  selectionMode,
  isSelected,
  onToggleSelect,
}) => {
  const { showAlert, AlertType } = useAlert();

  const currentUserId = useCustomSelector(getCurrentUserId);
  const clients = useCustomSelector((state) => getDashboardClients(state)) || [];
  const capacityTasks = useCustomSelector(getCareOrchestrationCapacityTasks) || [];
  const capacityTasksLoaded = useCustomSelector(getCareOrchestrationCapacityTasksLoaded);
  const currentUserClient = React.useMemo(
    () => clients.find((client) => `${client?.id ?? ''}` === `${currentUserId ?? ''}`) || null,
    [clients, currentUserId],
  );
  const currentUserName = getStaffDisplayName(currentUserClient);

  const [claimHover, setClaimHover] = React.useState(false);
  const claimHoverTimer = React.useRef(null);
  const openClaimBanner = () => {
    if (claimHoverTimer.current) clearTimeout(claimHoverTimer.current);
    setClaimHover(true);
  };
  const closeClaimBanner = () => {
    if (claimHoverTimer.current) clearTimeout(claimHoverTimer.current);
    claimHoverTimer.current = setTimeout(() => setClaimHover(false), 120);
  };

  const isTaskUnassigned = (candidateTask) => {
    const owner = `${candidateTask?.owner || ''}`.trim();
    return !owner || owner.toLowerCase() === 'unassigned';
  };

  const claimVerdict = React.useMemo(() => {
    if (!capacityTasksLoaded || !claimHover || !task || !isTaskUnassigned(task) || !currentUserName)
      return null;
    const proj = projectClaim(
      capacityTasks,
      task,
      'day',
      currentUserName,
      getStaffRole(currentUserClient),
      currentUserClient?.dailyCapacityMinutes,
      currentUserClient?.weeklyCapacityMinutes,
    );
    const breach = detectNewBreach(
      capacityTasks,
      task,
      currentUserName,
      getStaffRole(currentUserClient),
      currentUserClient?.dailyCapacityMinutes,
    );
    const isOver = proj.deltaMinutes > 0 && proj.projected.committed > proj.projected.available;
    if (!isOver && !breach.hasNewBreach) return null;
    return { proj, breach, isOver, isBreach: breach.hasNewBreach };
  }, [capacityTasks, capacityTasksLoaded, claimHover, task, currentUserName, currentUserClient]);

  if (!task) return null;

  const patient = task?.patient ?? getPatient(task?.patientId);
  const due = task?.due ? formatDue(task.due) : { text: '—', urgent: false };
  const displayStatus = getTaskDisplayStatus(task);
  const displayStatusLabel = getTaskDisplayStatusLabel(task);
  const isBlocked = isTaskBlockedStatus(task);
  const isEscalated = isTaskEscalatedStatus(task);
  const isCompleted = displayStatus === TASK_DISPLAY_STATUS.Done;
  const isUnableToComplete = isTaskUnableToComplete(task);
  const isLinked = isLinkedTask(task);
  const isBlockedByDependencies = isWorkflowBlockedTask(task);
  const isAutomaticTask = isAutomaticWorkflowTask(task);
  const taskPriority = task?.priority ?? TASK_PRIORITY.Low;
  const ownerLabel = `${task?.owner || ''}`.trim();
  const isUnassigned = !ownerLabel || ownerLabel.toLowerCase() === 'unassigned';
  const assignButtonLabel = isAssigning
    ? 'Assigning...'
    : claimVerdict
      ? 'Assign anyway'
      : 'Assign to me';

  const handleAssignClick = (event) => {
    event.stopPropagation();
    onAssignToMe?.(task.id);
  };

  const renderClaimBanner = () => {
    if (!claimVerdict) return null;
    const { proj, isOver, isBreach } = claimVerdict;
    const current = proj.current.committed;
    const projected = proj.projected.committed;
    const cap = proj.projected.available;
    const { deltaMinutes } = proj;
    const breachCount = claimVerdict.breach.count || 0;

    const barMax = Math.max(projected * 1.08, cap * 1.35, 1);
    const pct = (value) => (Math.max(0, value) / barMax) * 100;
    const capLeft = pct(cap);
    const nowAmberW = pct(Math.min(current, cap));
    const nowOverW = pct(Math.max(0, current - cap));
    const addAmberFrom = Math.min(current, cap);
    const addAmberW = pct(Math.max(0, Math.min(projected, cap) - addAmberFrom));
    const addOverFrom = Math.max(current, cap);
    const addOverW = pct(Math.max(0, projected - addOverFrom));

    return (
      <div
        className={`${styles.claimCapacityBanner} ${
          isBreach ? styles.claimCapacityBannerBreach : styles.claimCapacityBannerOver
        }`}
        onMouseEnter={openClaimBanner}
        onMouseLeave={closeClaimBanner}
      >
        <div className={styles.claimCapacityBannerRow}>
          <AlertTriangle
            className={`${styles.claimCapacityIcon} ${
              isBreach ? styles.claimCapacityIconBreach : styles.claimCapacityIconOver
            }`}
            strokeWidth={2}
          />
          <div className={styles.claimCapacityBannerText}>
            {isOver ? (
              <>
                Assigning this puts you <strong className={styles.tOver}>over capacity</strong> —{' '}
                <strong>{projected}</strong> / {cap}m today (+{deltaMinutes}m)
                {isBreach && breachCount > 0 ? (
                  <>
                    {' '}
                    and{' '}
                    <strong className={styles.tBreach}>
                      pushes {breachCount} of your {breachCount === 1 ? 'task' : 'tasks'} past SLA
                    </strong>
                  </>
                ) : null}
                .
              </>
            ) : (
              <>
                Claiming this <strong className={styles.tBreach}>breaks an SLA</strong> —{' '}
                <strong className={styles.tBreach}>
                  pushes {breachCount} of your {breachCount === 1 ? 'task' : 'tasks'} past SLA
                </strong>{' '}
                (you&rsquo;d be at {projected}/{cap}m today).
              </>
            )}
          </div>
        </div>
        <div className={styles.claimCapacityMeterWrap}>
          <div className={styles.claimCapacityTrack}>
            <div className={styles.claimCapacityFillAmber} style={{ width: `${nowAmberW}%` }} />
            {nowOverW > 0 ? (
              <div
                className={styles.claimCapacityFillRed}
                style={{ left: `${capLeft}%`, width: `${nowOverW}%` }}
              />
            ) : null}
            {addAmberW > 0 ? (
              <div
                className={styles.claimCapacityFillAmberHatch}
                style={{ left: `${pct(addAmberFrom)}%`, width: `${addAmberW}%` }}
              />
            ) : null}
            {addOverW > 0 ? (
              <div
                className={styles.claimCapacityFillRedHatch}
                style={{ left: `${pct(addOverFrom)}%`, width: `${addOverW}%` }}
              />
            ) : null}
            <div className={styles.claimCapacityTickNow} style={{ left: `${pct(current)}%` }} />
            <div className={styles.claimCapacityTickCap} style={{ left: `${capLeft}%` }} />
          </div>
          <div className={styles.claimCapacityLabels}>
            <span className={styles.claimCapacityLabelNow}>{current}m now</span>
            <span className={styles.claimCapacityLabelCap} style={{ left: `${capLeft}%` }}>
              capacity {cap}m
            </span>
          </div>
        </div>
      </div>
    );
  };

  const taskIsMessage = isMessageTask(task);
  const taskIsAssessment = isAssessmentTask(task);
  const taskTitle = getTaskDisplayTitle(task);
  const taskReasonLabel =
    `${task?.reason || ''}`.trim() || getTaskReasonById(task?.reasonId)?.label || '';
  const taskDescription = `${task?.taskDescription || task?.task_description || ''}`.trim();
  const fallbackMetaLabel = `${patient?.name || '-'} -> ${task?.owner || 'Unassigned'}`;
  const teamInfo = teamMeta[task?.team] || null;
  const teamIcon = task?.team === 'rcm' ? '◇' : teamInfo?.icon;
  const teamLabel = teamInfo?.label ? `${teamInfo.label} team` : null;
  const completer = clients.find((client) => client?.id === Number(task?.updatedBy));
  const completerName = compact && isCompleted ? completer?.getName?.(false, true) : null;
  const completedCaption =
    compact && isCompleted && (teamInfo || completerName) ? (
      <>
        {teamInfo ? `${teamIcon} ${teamInfo.label}` : null}
        {teamInfo && completerName ? ' · ' : null}
        {completerName}
      </>
    ) : null;
  const subtaskAssignmentOwner = ownerLabel || 'unassigned';
  const showPriorityBorder = !isCompleted;
  const priorityBorderClass = isSubtask
    ? styles.rowPrioritySubtask
    : isLinked
      ? styles.rowPriorityLinked
      : styles[`rowPriority${taskPriority}`] || '';
  const showCriticalIcon =
    !isLinked &&
    !isBlocked &&
    showPriorityBorder &&
    taskPriority === TASK_PRIORITY.Critical &&
    !taskIsMessage &&
    !taskIsAssessment;
  const taskDeepLinkUrl = getTaskDeepLinkUrl(task);

  const handleCopyTaskLink = async (event) => {
    event.stopPropagation();
    if (!taskDeepLinkUrl) return;

    try {
      const didCopy = await copyTextToClipboard(taskDeepLinkUrl);
      if (!didCopy) throw new Error('Copy failed');
      showAlert(AlertType.Success, '', 'Task link copied to clipboard.');
    } catch (error) {
      showAlert(AlertType.Error, '', 'Unable to copy task link.');
    }
  };

  const hasDelegatedSubtasks = delegatedSubtaskMeta?.hasSubtasks;
  const allDelegatedSubtasksComplete = delegatedSubtaskMeta?.allDone;
  const activeSubtaskChildCount = delegatedSubtaskMeta?.openCount ?? subtaskChildCount;
  const totalChildCount = linkedChildCount + activeSubtaskChildCount;
  const showLinkedToggle = !compact && totalChildCount > 0 && typeof onToggleLinked === 'function';
  const childToggleLabel = [
    linkedChildCount > 0
      ? `${linkedChildCount} linked ${linkedChildCount === 1 ? 'task' : 'tasks'}`
      : null,
    activeSubtaskChildCount > 0
      ? `${activeSubtaskChildCount} sub-${activeSubtaskChildCount === 1 ? 'task' : 'tasks'}`
      : null,
  ]
    .filter(Boolean)
    .join(', ');
  const showLinkedChildIcon = linkedChildCount > 0 && subtaskChildCount === 0;
  const showSubtaskChildIcon = activeSubtaskChildCount > 0 && linkedChildCount === 0;
  const showChildCountStatus =
    !isBlocked && !isEscalated && (linkedChildCount > 0 || activeSubtaskChildCount > 0);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => {
        if (selectionMode) {
          onToggleSelect?.(task.id);
        } else {
          onSelect?.(task);
        }
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          if (selectionMode) {
            onToggleSelect?.(task.id);
          } else {
            onSelect?.(task);
          }
        }
      }}
      className={[
        styles.row,
        compact ? styles.compact : '',
        isCompleted ? styles.completedRow : '',
        isUnableToComplete ? styles.unableToCompleteRow : '',
        isCompleted && isSubtask ? styles.completedSubtaskRow : '',
        isLinkedChild || isSubtaskChild ? styles.linkedChildRow : '',
        isSubtaskChild ? styles.subtaskChildRow : '',
        selected ? styles.selected : '',
        !compact && showPriorityBorder ? priorityBorderClass : '',
      ].join(' ')}
    >
      <div className={styles.taskCell}>
        <div className={styles.taskLine}>
          {selectionMode ? (
            <input
              type="checkbox"
              className={styles.selectionCheckbox}
              checked={!!isSelected}
              onChange={(event) => {
                event.stopPropagation();
                onToggleSelect?.(task.id);
              }}
              onClick={(event) => event.stopPropagation()}
              aria-label={`Select task: ${taskTitle}`}
            />
          ) : isCompleted ? (
            <div
              className={[
                styles.checkBox,
                taskPriority === TASK_PRIORITY.Critical ? styles.checkCritical : '',
                isBlocked ? styles.checkFlagged : '',
                isUnableToComplete ? styles.checkUnableToComplete : '',
              ].join(' ')}
            >
              <Check className={styles.checkIcon} strokeWidth={2.4} />
            </div>
          ) : isSubtask ? (
            <span className={styles.subtaskIcon} aria-hidden="true">
              <Hourglass className={styles.subtaskIconGlyph} strokeWidth={1.8} />
            </span>
          ) : isLinked ? (
            <span className={styles.linkedIcon} aria-hidden="true">
              <Link2 className={styles.linkedIconGlyph} strokeWidth={1.8} />
            </span>
          ) : taskIsMessage ? (
            <span className={styles.messageIcon} aria-hidden="true" />
          ) : taskIsAssessment ? (
            <span className={styles.assessmentIcon} aria-hidden="true" />
          ) : showCriticalIcon ? (
            <span className={styles.criticalIcon} aria-hidden="true">
              <AlertTriangle className={styles.criticalIconGlyph} strokeWidth={2.2} />
            </span>
          ) : (
            <div
              className={[
                styles.checkBox,
                taskPriority === TASK_PRIORITY.Critical ? styles.checkCritical : '',
                isBlocked ? styles.checkFlagged : '',
              ].join(' ')}
            />
          )}
          <div className={styles.main}>
            <div className={styles.titleRow}>
              <span
                className={`${styles.title} ${isCompleted && !compact ? styles.completedTitle : ''}`}
              >
                {taskTitle}
              </span>
              {isSubtask ? (
                <span className={styles.subtaskAssignment}>
                  <span className={styles.subtaskAssignmentArrow}>&rarr;</span>
                  {teamLabel ? (
                    <>
                      <span className={styles.subtaskAssignmentTeam}>
                        <span className={styles.subtaskAssignmentTeamIcon}>{teamIcon}</span>
                        <span>{teamLabel}</span>
                      </span>
                      <span className={styles.subtaskAssignmentDivider}>·</span>
                    </>
                  ) : null}
                  <span className={styles.subtaskAssignmentOwner}>{subtaskAssignmentOwner}</span>
                </span>
              ) : null}
              {task.mustHave2026 && (
                <Badge variant="edge" className={styles.edgeBadge}>
                  EDGE
                </Badge>
              )}
            </div>
            {!compact && isLinked && linkedParentTitle && !isSubtask ? (
              <div className={styles.linkedFromRow}>{`from: ${linkedParentTitle}`}</div>
            ) : null}
            {!compact && !isSubtask && taskReasonLabel ? (
              <div className={styles.reasonRow}>{taskReasonLabel}</div>
            ) : null}
            {(completedCaption || task.caption) && (
              <div className={`${styles.caption} ${compact ? styles.captionCompact : ''}`}>
                {completedCaption || task.caption}
              </div>
            )}
            {!compact && (
              <div className={styles.metaRow}>{taskDescription || fallbackMetaLabel}</div>
            )}
          </div>
          {!compact && (
            <span className={styles.rowActions}>
              <div
                className={styles.assignCell}
                onMouseEnter={openClaimBanner}
                onMouseLeave={closeClaimBanner}
              >
                {isAutomaticTask ? (
                  <span className={styles.systemBadge}>System</span>
                ) : isUnassigned && !selectionMode ? (
                  <button
                    type="button"
                    className={`${styles.assignButton} ${
                      claimVerdict?.isBreach
                        ? styles.assignButtonBreach
                        : claimVerdict
                          ? styles.assignButtonOver
                          : ''
                    }`}
                    disabled={isAssigning}
                    onClick={handleAssignClick}
                    onFocus={openClaimBanner}
                    onBlur={closeClaimBanner}
                  >
                    {assignButtonLabel}
                  </button>
                ) : null}
              </div>
              <button
                type="button"
                className={styles.copyButton}
                onClick={handleCopyTaskLink}
                disabled={!taskDeepLinkUrl}
                aria-label="Copy task link"
                title="Copy task link"
              >
                <Copy className={styles.copyIcon} strokeWidth={1.8} />
              </button>
            </span>
          )}
        </div>
        {!compact && isLinked && linkedParentTitle ? (
          <div className={styles.waitingOnRow}>
            <Link2 className={styles.waitingOnIcon} strokeWidth={1.8} />
            <span className={styles.waitingOnLabel}>
              {isBlockedByDependencies ? 'Blocked by:' : 'Waiting on:'}
            </span>
            <span className={styles.waitingOnTitle}>{linkedParentTitle}</span>
          </div>
        ) : null}
        {!compact && isSubtask && parentTitle ? (
          <div className={styles.subtaskOfRow}>
            <Hourglass className={styles.subtaskOfIcon} strokeWidth={1.8} />
            <span className={styles.subtaskOfTitle}>{`Sub-task of: ${parentTitle}`}</span>
          </div>
        ) : null}
        {!compact &&
        !isSubtask &&
        !isCompleted &&
        hasDelegatedSubtasks &&
        allDelegatedSubtasksComplete ? (
          <div className={styles.parentReadyRow}>
            <CheckCircle2 className={styles.parentReadyIcon} strokeWidth={1.8} />
            <span className={styles.parentReadyTitle}>
              All delegated sub-tasks complete · ready to continue
            </span>
          </div>
        ) : null}
        {!compact && !selectionMode ? renderClaimBanner() : null}
        {showLinkedToggle ? (
          <button
            type="button"
            className={styles.linkedToggle}
            onClick={(event) => {
              event.stopPropagation();
              onToggleLinked?.(task.id);
            }}
          >
            <ChevronRight
              className={`${styles.linkedToggleChevron} ${linkedExpanded ? styles.linkedToggleChevronOpen : ''}`}
              strokeWidth={1.8}
            />
            {showLinkedChildIcon ? (
              <Link2 className={styles.linkedToggleIcon} strokeWidth={1.8} />
            ) : null}
            {showSubtaskChildIcon ? (
              <Hourglass className={styles.linkedToggleIcon} strokeWidth={1.8} />
            ) : null}
            <span>{childToggleLabel}</span>
          </button>
        ) : null}
      </div>
      {!compact && (
        <>
          <div className={styles.statusCell}>
            {isBlocked ? (
              <Badge variant="critical">Blocked</Badge>
            ) : isEscalated ? (
              <Badge variant="escalated">Escalated</Badge>
            ) : showChildCountStatus ? (
              <Badge className={styles.childCountBadge}>
                {linkedChildCount > 0 ? (
                  <span className={styles.childCountBadgeItem}>
                    <Link2 className={styles.childCountBadgeIcon} strokeWidth={1.8} />
                    {linkedChildCount}
                  </span>
                ) : null}
                {linkedChildCount > 0 && activeSubtaskChildCount > 0 ? (
                  <span className={styles.childCountBadgeDivider}>·</span>
                ) : null}
                {activeSubtaskChildCount > 0 ? (
                  <span className={styles.childCountBadgeItem}>
                    <Hourglass className={styles.childCountBadgeIcon} strokeWidth={1.8} />
                    {activeSubtaskChildCount}
                  </span>
                ) : null}
              </Badge>
            ) : hasDelegatedSubtasks && allDelegatedSubtasksComplete && !isCompleted ? (
              <Badge variant="green">Ready</Badge>
            ) : isUnableToComplete || isTaskCanceled(task) ? (
              <Badge variant={isUnableToComplete ? 'orange' : 'default'}>
                {displayStatusLabel}
              </Badge>
            ) : null}
          </div>
          <div className={styles.priorityCell}>
            {isCompleted ? null : (
              <span
                className={`${styles.priorityBadge} ${styles[`priorityBadge${taskPriority}`] || ''}`}
              >
                {PRIORITY_BADGE_COPY[taskPriority] ?? taskPriority}
              </span>
            )}
          </div>
        </>
      )}
      <div className={compact ? undefined : styles.dueCell}>
        <span
          className={`${styles.dueText} ${
            isCompleted && !compact ? styles.dueDone : due.urgent ? styles.dueUrgent : ''
          }`}
        >
          {isCompleted ? (compact ? moment(task.updatedAt).format('MMM D') : 'Done') : due.text}
        </span>
      </div>
    </div>
  );
};

TaskRow.propTypes = {
  task: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    patient: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
    }),
    patientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    due: PropTypes.string,
    priority: PropTypes.string,
    status: PropTypes.number,
    team: PropTypes.string,
    owner: PropTypes.string,
    updatedBy: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    updatedAt: PropTypes.string,
    reason: PropTypes.string,
    taskDescription: PropTypes.string,
    task_description: PropTypes.string,
    typeId: PropTypes.number,
    reasonId: PropTypes.number,
    title: PropTypes.string,
    assessmentType: PropTypes.string,
    assessmentVariant: PropTypes.string,
    mustHave2026: PropTypes.bool,
    caption: PropTypes.string,
    isVisible: PropTypes.bool,
    stepType: PropTypes.string,
    dependency: PropTypes.object,
  }),
  compact: PropTypes.bool,
  onSelect: PropTypes.func,
  selected: PropTypes.bool,
  onAssignToMe: PropTypes.func,
  isAssigning: PropTypes.bool,
  isLinkedChild: PropTypes.bool,
  isSubtaskChild: PropTypes.bool,
  isSubtask: PropTypes.bool,
  parentTitle: PropTypes.string,
  linkedParentTitle: PropTypes.string,
  linkedChildCount: PropTypes.number,
  subtaskChildCount: PropTypes.number,
  delegatedSubtaskMeta: PropTypes.shape({
    totalCount: PropTypes.number,
    openCount: PropTypes.number,
    doneCount: PropTypes.number,
    hasSubtasks: PropTypes.bool,
    waitingOnSubtasks: PropTypes.bool,
    allDone: PropTypes.bool,
    openSubtasks: PropTypes.arrayOf(PropTypes.object),
  }),
  linkedExpanded: PropTypes.bool,
  onToggleLinked: PropTypes.func,
  selectionMode: PropTypes.bool,
  isSelected: PropTypes.bool,
  onToggleSelect: PropTypes.func,
};

export default TaskRow;
